import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import '../LoopinoDesktopGuidelines.css';
import './DesktopProfileSettingsPage.css';
import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import { useAuthContext } from '../../../context/AuthContext';
import { Skeleton } from '../../effects';
import { DesktopAlertProvider } from '../common/alerts/DesktopAlertProvider';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';

const DEFAULT_LANGUAGE = 'it';
const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/Rome',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Denver',
  'America/Sao_Paulo',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Australia/Sydney'
];

const mapUserProfile = (user, fallbackLanguage = DEFAULT_LANGUAGE) => {
  if (!user || typeof user !== 'object') {
    return {
      email: '',
      username: '',
      firstName: '',
      lastName: '',
      timezone: '',
      notifications: false,
      emailNotifications: false,
      language: fallbackLanguage,
      verified: false,
      lastLogin: null,
      updatedAt: null,
      aiStatus: null
    };
  }

  const safeProfile = user.profile || {};
  const safeSettings = user.settings || {};
  const notifications = Boolean(
    safeSettings.notifications ?? safeSettings.push_notifications ?? safeSettings.pushNotifications ?? false
  );
  const emailNotifications = Boolean(
    safeSettings.email_notifications ?? safeSettings.emailNotifications ?? false
  );

  return {
    email: user.email || '',
    username: user.username || '',
    firstName: user.first_name || user.firstName || '',
    lastName: user.last_name || user.lastName || '',
    timezone: safeProfile.timezone || '',
    notifications,
    emailNotifications,
    language: user.language || fallbackLanguage,
    verified: user.is_verified ?? user.isVerified ?? false,
    lastLogin: user.last_login || user.lastLogin || null,
    updatedAt: user.updated_at || user.updatedAt || null,
    aiStatus: user.ai_battery?.status || user.aiBattery?.status || null
  };
};

const persistUserLocally = (user) => {
  return user;
};

const formatDateTime = (value) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ChevronDownIcon = ({ className = '' }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DropdownSelect = ({ value, options = [], placeholder, onChange }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyles, setMenuStyles] = useState({ top: 0, left: 0, width: undefined });

  const selectedLabel = useMemo(() => {
    const match = options.find((item) => (item.value ?? item.id) === value);
    return match?.label || match?.value || match?.id || '';
  }, [options, value]);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 6;
    const width = rect.width;
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(Math.max(rect.left, 8), maxLeft);
    setMenuStyles({
      top: rect.bottom + gap,
      left,
      width,
      minWidth: width
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();

    const handleClickOutside = (event) => {
      const triggerEl = triggerRef.current;
      const menuEl = menuRef.current;
      if (
        (triggerEl && triggerEl.contains(event.target)) ||
        (menuEl && menuEl.contains(event.target))
      ) {
        return;
      }
      setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    const handleWindowChange = () => updateMenuPosition();

    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [open, updateMenuPosition]);

  return (
    <div className="loopino-dna-dropdown" ref={triggerRef}>
      <button
        type="button"
        className="loopino-dna-dropdown-trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span>{selectedLabel || placeholder}</span>
        <ChevronDownIcon className={`loopino-new-strategy-chevron ${open ? 'is-open' : ''}`} />
      </button>
      {open && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          className="loopino-dna-dropdown-menu"
          ref={menuRef}
          style={{
            position: 'fixed',
            zIndex: 1000,
            ...menuStyles
          }}
        >
          {options.map((option) => {
            const optionValue = option.value ?? option.id ?? option.label;
            const isSelected = optionValue === value;
            return (
              <button
                key={optionValue}
                type="button"
                className="loopino-dna-dropdown-item"
                data-selected={isSelected || undefined}
                onClick={() => {
                  onChange(optionValue);
                  setOpen(false);
                }}
              >
                {option.label || option.value || option.id}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

const getTimezoneOffsetLabel = (timezone) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(new Date());
    const timeZoneName = parts.find((part) => part.type === 'timeZoneName')?.value || '';
    if (timeZoneName) {
      return timeZoneName.replace('GMT', 'UTC');
    }
  } catch (error) {
    // ignore invalid timezone
  }
  return 'UTC';
};

const buildTimezoneOptions = () => {
  let zones = [];
  if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
    zones = Intl.supportedValuesOf('timeZone');
  } else {
    zones = FALLBACK_TIMEZONES;
  }

  const uniqueZones = Array.from(new Set(zones));
  return uniqueZones
    .map((zone) => {
      const offset = getTimezoneOffsetLabel(zone);
      return {
        value: zone,
        label: `${zone} (${offset})`
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

const DesktopProfileSettingsContent = () => {
  const {
    t,
    language,
    availableLanguages,
    setLanguage,
    isUpdating: isLanguageUpdating,
    lastError: languageError
  } = useLanguage();
  const { notify } = useDesktopAlert();
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const { changePassword, updateUser } = useAuthContext();
  const { makeRequest } = useApi();

  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    timezone: ''
  });
  const [preferencesForm, setPreferencesForm] = useState({
    language: language || DEFAULT_LANGUAGE,
    notifications: false,
    emailNotifications: false
  });
  const [meta, setMeta] = useState({
    verified: false,
    lastLogin: null,
    updatedAt: null,
    aiStatus: null
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [saving, setSaving] = useState({
    profile: false,
    preferences: false,
    password: false
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [timezoneOptions] = useState(buildTimezoneOptions);
  const originalIdentityRef = useRef({ email: '', username: '' });
  const timezoneOptionsWithCurrent = useMemo(() => {
    if (profileForm.timezone && !timezoneOptions.some((option) => option.value === profileForm.timezone)) {
      return [{ value: profileForm.timezone, label: profileForm.timezone }, ...timezoneOptions];
    }
    return timezoneOptions;
  }, [profileForm.timezone, timezoneOptions]);

  const languageOptions = useMemo(() => {
    if (Array.isArray(availableLanguages) && availableLanguages.length > 0) {
      return availableLanguages.map((lang) => ({
        value: lang.value ?? lang.code ?? lang.id,
        label: lang.label ?? lang.name ?? lang.value ?? lang.code
      }));
    }
    return [
      { value: 'it', label: t('settings.language.options.it', { defaultValue: 'Italiano' }) },
      { value: 'en', label: t('settings.language.options.en', { defaultValue: 'English' }) }
    ];
  }, [availableLanguages, t]);

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const response = await makeRequest('/api/v1/user/profile', { method: 'GET' });
      const user = response?.user || response?.data?.user || response?.data || response;
      const normalized = mapUserProfile(user, DEFAULT_LANGUAGE);

      setProfileForm({
        firstName: normalized.firstName,
        lastName: normalized.lastName,
        email: normalized.email,
        username: normalized.username,
        timezone: normalized.timezone
      });
      originalIdentityRef.current = {
        email: normalized.email,
        username: normalized.username
      };
      setPreferencesForm((prev) => ({
        ...prev,
        language: normalized.language || prev.language || DEFAULT_LANGUAGE,
        notifications: normalized.notifications,
        emailNotifications: normalized.emailNotifications
      }));
      setMeta({
        verified: normalized.verified,
        lastLogin: normalized.lastLogin,
        updatedAt: normalized.updatedAt,
        aiStatus: normalized.aiStatus
      });
    } catch (error) {
      notify({
        variant: 'error',
        title: error.message || tRef.current('settings.profile.errors.loadProfile', { defaultValue: 'Impossibile caricare il profilo utente.' })
      });
    } finally {
      setProfileLoading(false);
    }
  }, [makeRequest, notify]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!language) return;
    setPreferencesForm((prev) => ({
      ...prev,
      language: prev.language || language
    }));
  }, [language]);

  useEffect(() => {
    if (!languageError) return;
    notify({
      variant: 'error',
      title: t('settings.profile.languageError', { defaultValue: 'Errore nel salvataggio della lingua.' })
    });
  }, [languageError, notify, t]);

  const handleProfileFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleTimezoneSelect = useCallback((value) => {
    setProfileForm((prev) => ({ ...prev, timezone: value }));
  }, []);

  const handleLanguageSelect = useCallback((value) => {
    setPreferencesForm((prev) => ({ ...prev, language: value }));
    if (value && value !== language && typeof setLanguage === 'function') {
      setLanguage(value);
    }
  }, [language, setLanguage]);

  const handlePreferencesChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'notifications' || name === 'emailNotifications') {
      if (checked) {
        notify({
          variant: 'warning',
          title: t('settings.profile.notificationsUnavailableTitle', { defaultValue: 'Notifiche non disponibili' }),
          description: t('settings.profile.notificationsUnavailableDescription', {
            defaultValue: 'Le notifiche push ed email non sono ancora attive su desktop.'
          })
        });
        setPreferencesForm((prev) => ({ ...prev, [name]: false }));
        return;
      }
    }

    setPreferencesForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, [notify, t]);

  const handlePasswordFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleProfileSubmit = useCallback(async (event) => {
    event.preventDefault();
    setSaving((prev) => ({ ...prev, profile: true }));

    const trimmedEmail = profileForm.email?.trim() || '';
    const trimmedUsername = profileForm.username?.trim() || '';
    const identityChanged = trimmedEmail !== (originalIdentityRef.current.email || '') ||
      trimmedUsername !== (originalIdentityRef.current.username || '');
    let identityUser = null;
    let identityUpdated = false;

    if (identityChanged) {
      if (typeof updateUser === 'function') {
        try {
          const response = await updateUser({
            email: trimmedEmail || undefined,
            username: trimmedUsername || undefined
          });
          identityUser = response?.user || response?.data?.user || response?.data || response;
          identityUpdated = true;
        } catch (error) {
          notify({
            variant: 'error',
            title: error.message || t('settings.profile.identityUpdateFailed', {
              defaultValue: 'Impossibile aggiornare email o username.'
            })
          });
        }
      } else {
        notify({
          variant: 'error',
          title: t('settings.profile.identityUpdateUnavailable', {
            defaultValue: 'Aggiornamento email/username non disponibile.'
          })
        });
      }
    }

    let profileUser = null;
    try {
      const payload = {
        first_name: profileForm.firstName?.trim(),
        last_name: profileForm.lastName?.trim(),
        profile: {
          timezone: profileForm.timezone
        }
      };

      if (!payload.first_name) {
        delete payload.first_name;
      }
      if (!payload.last_name) {
        delete payload.last_name;
      }
      if (!payload.profile.timezone) {
        delete payload.profile.timezone;
      }
      if (Object.keys(payload.profile).length === 0) {
        delete payload.profile;
      }

      const response = await makeRequest('/api/v1/user/profile', {
        method: 'PUT',
        data: payload
      });

      profileUser = response?.user || response?.data?.user || response?.data || response;
      const mergedUser = { ...(profileUser || {}), ...(identityUser || {}) };
      const normalized = mapUserProfile(mergedUser, preferencesForm.language || language || DEFAULT_LANGUAGE);

      setProfileForm({
        firstName: normalized.firstName,
        lastName: normalized.lastName,
        email: normalized.email,
        username: normalized.username,
        timezone: normalized.timezone
      });
      originalIdentityRef.current = {
        email: normalized.email,
        username: normalized.username
      };
      setPreferencesForm((prev) => ({
        ...prev,
        notifications: normalized.notifications,
        emailNotifications: normalized.emailNotifications
      }));
      setMeta({
        verified: normalized.verified,
        lastLogin: normalized.lastLogin,
        updatedAt: normalized.updatedAt,
        aiStatus: normalized.aiStatus
      });
      persistUserLocally(mergedUser);

      notify({
        variant: 'success',
        title: t('settings.profile.updated', { defaultValue: 'Profilo aggiornato con successo.' })
      });
    } catch (error) {
      notify({
        variant: 'error',
        title: error.message || t('settings.profile.errors.saveFailed', { defaultValue: 'Salvataggio non riuscito.' })
      });
    } finally {
      if (!profileUser && identityUpdated) {
        const fallbackUser = identityUser || {
          email: trimmedEmail,
          username: trimmedUsername
        };
        const normalized = mapUserProfile(fallbackUser, preferencesForm.language || language || DEFAULT_LANGUAGE);
        setProfileForm((prev) => ({
          ...prev,
          email: normalized.email,
          username: normalized.username
        }));
        originalIdentityRef.current = {
          email: normalized.email,
          username: normalized.username
        };
        persistUserLocally(fallbackUser);
        notify({
          variant: 'success',
          title: t('settings.profile.identityUpdated', {
            defaultValue: 'Email e username aggiornati.'
          })
        });
      }
      setSaving((prev) => ({ ...prev, profile: false }));
    }
  }, [language, makeRequest, notify, preferencesForm.language, profileForm.email, profileForm.firstName, profileForm.lastName, profileForm.timezone, profileForm.username, t, updateUser]);

  const handlePreferencesSubmit = useCallback(async (event) => {
    event.preventDefault();
    setSaving((prev) => ({ ...prev, preferences: true }));

    try {
      const payload = {
        settings: {
          notifications: preferencesForm.notifications,
          email_notifications: preferencesForm.emailNotifications
        }
      };

      const response = await makeRequest('/api/v1/user/profile', {
        method: 'PUT',
        data: payload
      });

      const user = response?.user || response?.data?.user || response?.data || response;
      const selectedLanguage = preferencesForm.language || language || DEFAULT_LANGUAGE;
      const normalized = mapUserProfile(user, selectedLanguage);

      setPreferencesForm((prev) => ({
        ...prev,
        language: selectedLanguage,
        notifications: normalized.notifications,
        emailNotifications: normalized.emailNotifications
      }));
      setMeta({
        verified: normalized.verified,
        lastLogin: normalized.lastLogin,
        updatedAt: normalized.updatedAt,
        aiStatus: normalized.aiStatus
      });
      persistUserLocally(user ? { ...user, language: selectedLanguage } : user);

      notify({
        variant: 'success',
        title: t('settings.profile.preferencesUpdated', { defaultValue: 'Preferenze aggiornate.' })
      });
    } catch (error) {
      notify({
        variant: 'error',
        title: error.message || t('settings.profile.errors.updatePreferences', { defaultValue: 'Impossibile aggiornare le preferenze.' })
      });
    } finally {
      setSaving((prev) => ({ ...prev, preferences: false }));
    }
  }, [language, makeRequest, notify, preferencesForm.emailNotifications, preferencesForm.language, preferencesForm.notifications, t]);

  const handlePasswordSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      notify({
        variant: 'error',
        title: t('settings.profile.passwordRequired', { defaultValue: 'Compila tutti i campi password.' })
      });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      notify({
        variant: 'error',
        title: t('settings.profile.passwordMismatch', { defaultValue: 'Le password non coincidono.' })
      });
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      notify({
        variant: 'error',
        title: t('settings.profile.passwordLength', { defaultValue: 'Usa almeno 8 caratteri.' })
      });
      return;
    }

    setSaving((prev) => ({ ...prev, password: true }));

    try {
      const payload = {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      };

      if (typeof changePassword === 'function') {
        await changePassword(payload);
      } else {
        await makeRequest('/api/v1/auth/change-password', {
          method: 'POST',
          data: payload
        });
      }

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      notify({
        variant: 'success',
        title: t('settings.profile.passwordUpdated', { defaultValue: 'Password aggiornata.' })
      });
    } catch (error) {
      notify({
        variant: 'error',
        title: error.message || t('settings.profile.errors.updatePassword', { defaultValue: 'Impossibile aggiornare la password.' })
      });
    } finally {
      setSaving((prev) => ({ ...prev, password: false }));
    }
  }, [changePassword, makeRequest, notify, passwordForm.confirmPassword, passwordForm.currentPassword, passwordForm.newPassword, t]);

  const renderLoadingSkeleton = () => (
    <div className="loopino-desktop-shell loopino-profile-settings-shell">
      <header className="loopino-desktop-post-review-header loopino-profile-settings-header">
        <div>
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <Skeleton width="60%" height={36} shimmer />
          <Skeleton width="80%" height={18} shimmer style={{ marginTop: 12 }} />
        </div>
      </header>

      <div className="loopino-profile-settings-grid">
        {[0, 1].map((key) => (
          <section key={key} className="loopino-card loopino-profile-card">
            <Skeleton width="40%" height={20} shimmer style={{ marginBottom: 12 }} />
            <Skeleton width="70%" height={20} shimmer style={{ marginBottom: 12 }} />
            <div className="loopino-profile-input-row">
              <Skeleton width="100%" height={52} shimmer />
              <Skeleton width="100%" height={52} shimmer />
            </div>
            <Skeleton width="100%" height={120} shimmer />
          </section>
        ))}
      </div>
    </div>
  );

  if (profileLoading) {
    return renderLoadingSkeleton();
  }

  return (
    <div className="loopino-desktop-shell loopino-profile-settings-shell">
      <header className="loopino-desktop-post-review-header loopino-profile-settings-header">
        <div>
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <h1>{t('settings.profile.title', { defaultValue: 'Impostazioni account' })}</h1>
          <p className="loopino-desktop-post-review-summary">
            {t('settings.profile.subtitle', {
              defaultValue: 'Aggiorna dati utente, email e password dalla nuova pagina desktop pensata per le impostazioni.'
            })}
          </p>
        </div>
      </header>

      <div className="loopino-profile-settings-grid">
        <section className="loopino-card loopino-profile-card">
          <div className="loopino-card-header">
            <div className="loopino-stack" data-gap="sm">
              <p className="loopino-eyebrow">{t('settings.profile.sectionProfile', { defaultValue: 'Profilo' })}</p>
              <h2 className="loopino-card-title">
                {t('settings.profile.identity', { defaultValue: 'Dati utente' })}
              </h2>
              <p className="loopino-card-description">
                {t('settings.profile.identityHint', {
                  defaultValue: 'Nome, cognome, email e username saranno usati per comunicazioni e credenziali.'
                })}
              </p>
            </div>
          </div>

          <form className="loopino-form" onSubmit={handleProfileSubmit}>
            <div className="loopino-profile-input-row">
              <div className="loopino-form-field">
                <label className="loopino-form-label" htmlFor="firstName">
                  {t('settings.profile.firstName', { defaultValue: 'Nome' })}
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  className="loopino-input"
                  value={profileForm.firstName}
                  onChange={handleProfileFieldChange}
                  placeholder={t('settings.profile.placeholders.firstName', { defaultValue: 'Mario' })}
                  autoComplete="given-name"
                />
              </div>
              <div className="loopino-form-field">
                <label className="loopino-form-label" htmlFor="lastName">
                  {t('settings.profile.lastName', { defaultValue: 'Cognome' })}
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  className="loopino-input"
                  value={profileForm.lastName}
                  onChange={handleProfileFieldChange}
                  placeholder={t('settings.profile.placeholders.lastName', { defaultValue: 'Rossi' })}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="loopino-profile-input-row">
              <div className="loopino-form-field">
                <label className="loopino-form-label" htmlFor="email">
                  {t('settings.profile.email', { defaultValue: 'Email' })}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="loopino-input"
                  value={profileForm.email}
                  onChange={handleProfileFieldChange}
                  placeholder={t('settings.profile.placeholders.email', { defaultValue: 'you@example.com' })}
                  autoComplete="email"
                />
              </div>
              <div className="loopino-form-field">
                <label className="loopino-form-label" htmlFor="username">
                  {t('settings.profile.username', { defaultValue: 'Username' })}
                </label>
                <input
                  id="username"
                  name="username"
                  className="loopino-input"
                  value={profileForm.username}
                  onChange={handleProfileFieldChange}
                  placeholder={t('settings.profile.placeholders.username', { defaultValue: 'loopino_user' })}
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="loopino-profile-input-row">
              <div className="loopino-form-field">
                <label className="loopino-form-label" htmlFor="timezone">
                  {t('settings.profile.timezone', { defaultValue: 'Fuso orario' })}
                </label>
                <DropdownSelect
                  value={profileForm.timezone}
                  options={timezoneOptionsWithCurrent}
                  placeholder={t('settings.profile.timezonePlaceholder', { defaultValue: 'Seleziona un fuso orario' })}
                  onChange={handleTimezoneSelect}
                />
              </div>
            </div>

            <div className="loopino-profile-actions">
              <button
                type="submit"
                className="loopino-button"
                data-variant="primary"
                disabled={saving.profile}
              >
                {saving.profile
                  ? t('common.loading', { defaultValue: 'Salvataggio...' })
                  : t('settings.profile.saveProfile', { defaultValue: 'Salva dati' })}
              </button>
            </div>
          </form>
        </section>

        <section className="loopino-card loopino-profile-card">
          <div className="loopino-card-header">
            <div className="loopino-stack" data-gap="sm">
              <p className="loopino-eyebrow">{t('settings.profile.sectionPreferences', { defaultValue: 'Preferenze' })}</p>
              <h2 className="loopino-card-title">
                {t('settings.profile.preferencesTitle', { defaultValue: 'Lingua e notifiche' })}
              </h2>
              <p className="loopino-card-description">
                {t('settings.profile.preferencesHint', {
                  defaultValue: 'Scegli la lingua dell’interfaccia e gestisci le notifiche email.'
                })}
              </p>
            </div>
          </div>

          <form className="loopino-form" onSubmit={handlePreferencesSubmit}>
            <div className="loopino-profile-input-row">
              <div className="loopino-form-field">
                <label className="loopino-form-label" htmlFor="language">
                  {t('settings.profile.language', { defaultValue: 'Lingua' })}
                </label>
                <DropdownSelect
                  value={preferencesForm.language}
                  options={languageOptions}
                  placeholder={t('settings.profile.languagePlaceholder', { defaultValue: 'Seleziona lingua' })}
                  onChange={handleLanguageSelect}
                />
              </div>
            </div>

            <div className="loopino-profile-toggle-group">
              <div className="loopino-profile-toggle-row">
                <div>
                  <p className="loopino-profile-toggle-title">
                    {t('settings.profile.notifications', { defaultValue: 'Notifiche push' })}
                  </p>
                  <p className="loopino-profile-toggle-description">
                    {t('settings.profile.notificationsHint', { defaultValue: 'Ricevi aggiornamenti sull’attività del tuo account.' })}
                  </p>
                </div>
                <label className="loopino-profile-toggle">
                  <input
                    type="checkbox"
                    name="notifications"
                    checked={preferencesForm.notifications}
                    onChange={handlePreferencesChange}
                  />
                  <span className="loopino-profile-toggle-pill" aria-hidden="true" />
                </label>
              </div>

              <div className="loopino-profile-toggle-row">
                <div>
                  <p className="loopino-profile-toggle-title">
                    {t('settings.profile.emailNotifications', { defaultValue: 'Notifiche email' })}
                  </p>
                  <p className="loopino-profile-toggle-description">
                    {t('settings.profile.emailNotificationsHint', { defaultValue: 'Avvisi su campagne, password e attività di sicurezza.' })}
                  </p>
                </div>
                <label className="loopino-profile-toggle">
                  <input
                    type="checkbox"
                    name="emailNotifications"
                    checked={preferencesForm.emailNotifications}
                    onChange={handlePreferencesChange}
                  />
                  <span className="loopino-profile-toggle-pill" aria-hidden="true" />
                </label>
              </div>
            </div>

            <div className="loopino-profile-actions">
              <button
                type="submit"
                className="loopino-button"
                data-variant="primary"
                disabled={saving.preferences || isLanguageUpdating}
              >
                {saving.preferences
                  ? t('common.loading', { defaultValue: 'Salvataggio...' })
                  : t('settings.profile.savePreferences', { defaultValue: 'Salva preferenze' })}
              </button>
            </div>
          </form>
        </section>
      </div>

      <div className="loopino-profile-settings-grid loopino-profile-secondary-grid">
        <section className="loopino-glass-panel loopino-profile-card">
          <div className="loopino-card-header">
            <div className="loopino-stack" data-gap="sm">
              <p className="loopino-eyebrow">{t('settings.profile.security', { defaultValue: 'Sicurezza' })}</p>
              <h2 className="loopino-card-title">
                {t('settings.profile.passwordTitle', { defaultValue: 'Cambia password' })}
              </h2>
              <p className="loopino-card-description">
                {t('settings.profile.passwordHint', { defaultValue: 'Imposta una password robusta per proteggere l’account.' })}
              </p>
            </div>
          </div>

          <form className="loopino-form" onSubmit={handlePasswordSubmit}>
            <div className="loopino-profile-password-grid">
              <div className="loopino-form-field">
                <label className="loopino-form-label" htmlFor="currentPassword">
                  {t('settings.profile.currentPassword', { defaultValue: 'Password attuale' })}
                </label>
                <input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  className="loopino-input"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordFieldChange}
                  autoComplete="current-password"
                />
              </div>
              <div className="loopino-form-field">
                <label className="loopino-form-label" htmlFor="newPassword">
                  {t('settings.profile.newPassword', { defaultValue: 'Nuova password' })}
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  className="loopino-input"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordFieldChange}
                  autoComplete="new-password"
                />
              </div>
              <div className="loopino-form-field">
                <label className="loopino-form-label" htmlFor="confirmPassword">
                  {t('settings.profile.confirmPassword', { defaultValue: 'Conferma password' })}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  className="loopino-input"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordFieldChange}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="loopino-profile-actions">
              <button
                type="submit"
                className="loopino-button"
                data-variant="primary"
                disabled={saving.password}
              >
                {saving.password
                  ? t('common.loading', { defaultValue: 'Aggiornamento...' })
                  : t('settings.profile.savePassword', { defaultValue: 'Aggiorna password' })}
              </button>
            </div>
          </form>
        </section>

        <section className="loopino-card loopino-profile-card loopino-legal-card">
          <div className="loopino-card-header">
            <div className="loopino-stack" data-gap="sm">
              <p className="loopino-eyebrow">{t('settings.profile.legalEyebrow', { defaultValue: 'Legale' })}</p>
              <h2 className="loopino-card-title">
                {t('settings.profile.legalTitle', { defaultValue: 'Documenti legali' })}
              </h2>
              <p className="loopino-card-description">
                {t('settings.profile.legalHint', { defaultValue: 'Consulta privacy, termini e cookie della piattaforma.' })}
              </p>
            </div>
          </div>
          <div className="loopino-legal-links">
            <a className="loopino-legal-link" href="/privacy">
              <span>{t('settings.profile.legalLinks.privacy', { defaultValue: 'Privacy Policy' })}</span>
              <span className="loopino-legal-link-arrow">&gt;</span>
            </a>
            <a className="loopino-legal-link" href="/terms">
              <span>{t('settings.profile.legalLinks.terms', { defaultValue: 'Termini di Servizio' })}</span>
              <span className="loopino-legal-link-arrow">&gt;</span>
            </a>
            <a className="loopino-legal-link" href="/cookies">
              <span>{t('settings.profile.legalLinks.cookies', { defaultValue: 'Cookie Policy' })}</span>
              <span className="loopino-legal-link-arrow">&gt;</span>
            </a>
            <a className="loopino-legal-link" href="/legal">
              <span>{t('settings.profile.legalLinks.notice', { defaultValue: 'Note legali' })}</span>
              <span className="loopino-legal-link-arrow">&gt;</span>
            </a>
          </div>
        </section>

        <section className="loopino-card loopino-profile-card loopino-profile-meta-card">
          <div className="loopino-card-header">
            <div className="loopino-stack" data-gap="sm">
              <p className="loopino-eyebrow">{t('settings.profile.accountInfo', { defaultValue: 'Stato account' })}</p>
              <h2 className="loopino-card-title">
                {t('settings.profile.accountSummary', { defaultValue: 'Riepilogo' })}
              </h2>
              <p className="loopino-card-description">
                {t('settings.profile.accountHint', { defaultValue: 'Controlla accessi recenti e ultimo aggiornamento profilo.' })}
              </p>
            </div>
          </div>

          <div className="loopino-profile-meta-grid">
            <div>
              <p className="loopino-profile-meta-label">{t('settings.profile.lastLogin', { defaultValue: 'Ultimo accesso' })}</p>
              <p className="loopino-profile-meta-value">{formatDateTime(meta.lastLogin)}</p>
            </div>
            <div>
              <p className="loopino-profile-meta-label">{t('settings.profile.lastUpdate', { defaultValue: 'Ultimo aggiornamento' })}</p>
              <p className="loopino-profile-meta-value">{formatDateTime(meta.updatedAt)}</p>
            </div>
            <div>
              <p className="loopino-profile-meta-label">{t('settings.profile.currentEmail', { defaultValue: 'Email attuale' })}</p>
              <p className="loopino-profile-meta-value">{profileForm.email || '—'}</p>
            </div>
            <div>
              <p className="loopino-profile-meta-label">{t('settings.profile.currentLanguage', { defaultValue: 'Lingua' })}</p>
              <p className="loopino-profile-meta-value">{preferencesForm.language?.toUpperCase() || DEFAULT_LANGUAGE.toUpperCase()}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const DesktopProfileSettingsPage = (props) => (
  <DesktopAlertProvider>
    <DesktopProfileSettingsContent {...props} />
  </DesktopAlertProvider>
);

export default DesktopProfileSettingsPage;
